/**
 * Main menu panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import { PSBackground } from "./client-core";
import {
	Config, PS, PSRoom, type PSRoomFocusOptions, type RoomID, type RoomOptions, type Team, consoleModeOn,
} from "./client-main";
import { PSIcon, PSPanelErrorBoundary, PSPanelWrapper, PSRoomPanel, PSView, ReconnectTimer } from "./panels";
import type { BattlesRoom } from "./panel-battle";
import type { ChatRoom } from "./panel-chat";
import type { LadderFormatRoom } from "./panel-ladder";
import type { RoomsRoom } from "./panel-rooms";
import { TeamBox, type SelectType } from "./panel-teamdropdown";
import { Dex, toID, type ID } from "./battle-dex";
import type { Args } from "./battle-text-parser";
import { BattleLog } from "./battle-log"; // optional
import { Teams } from "./battle-teams";
import { interfaceTheme } from "./signal-palettes";
import { PSConsole } from "./console-nav";
import { ConsoleCloseButton, ConsoleScreenHead, menuScreensOn } from "./console-screens";
import { PSTabletop, type TabletopBracketMatch, type TabletopTournament } from "./tabletop-link";

export type RoomInfo = {
	title: string, id?: RoomID, desc?: string, userCount?: number, section?: string, privacy?: 'hidden',
	spotlight?: string, subRooms?: string[],
};

export class MainMenuRoom extends PSRoom {
	override readonly classType: string = 'mainmenu';
	listeners: Record<string, ((response: any) => void)[] | null> = {};
	userdetailsCache: {
		[userid: string]: {
			userid: ID,
			name: string,
			avatar?: string | number,
			status?: string,
			group?: string,
			customgroup?: string,
			rooms?: { [roomid: string]: { isPrivate?: true, p1?: string, p2?: string } },
		},
	} = {};
	roomsCache: {
		battleCount?: number,
		userCount?: number,
		chat?: RoomInfo[],
		sectionTitles?: string[],
	} = {};
	searchCountdown: { format: string, packedTeam: string, countdown: number, timer: number } | null = null;
	/**
	 * Everyone online and the battles they are in (/cmd onlinetrainers,
	 * server/chat-plugins/campaign-home.ts). Null until the first answer.
	 * The Battle! button and the right panel are built from it.
	 */
	trainersCache: OnlineTrainer[] | null = null;
	trainersTimer: ReturnType<typeof setInterval> | null = null;
	/** Friends and friend requests (/cmd friendsdata), for Console mode's Friends screen. */
	friendsCache: ConsoleFriendsData | null = null;
	requestFriends() {
		if (PS.user.named && !PS.isOffline) PS.send(`/cmd friendsdata`);
	}
	requestTrainers() {
		if (PS.user.named && !PS.isOffline) PS.send(`/cmd onlinetrainers`);
		void PSTabletop.loadTournaments();
	}
	/** Keep the trainers list fresh while the client is open and visible. */
	watchTrainers() {
		if (this.trainersTimer) return;
		this.requestTrainers();
		this.trainersTimer = setInterval(() => {
			if (!document.hidden) this.requestTrainers();
		}, 15000);
	}
	/** used to track the moment between "search sent" and "server acknowledged search sent" */
	teamSent: string | null = null;
	search: { searching: string[], games: Record<RoomID, string> | null } = { searching: [], games: null };
	disallowSpectators: boolean | null = PS.prefs.disallowspectators;
	lastChallenged: number | null = null;
	constructor(options: RoomOptions) {
		super(options);
		if (this.backlog) {
			// these aren't set yet, but a lot of things could go wrong if we don't
			PS.rooms[''] = this;
			PS.mainmenu = this;
			for (const args of this.backlog) {
				this.receiveLine(args);
			}
			this.backlog = null;
		}
	}
	adjustPrivacy() {
		PS.prefs.set('disallowspectators', this.disallowSpectators);
		if (this.disallowSpectators) return '/noreply /hidenext \n';
		return '';
	}
	startSearch = (format: string, team?: Team, parentElem?: HTMLElement | null) => {
		PS.requestNotifications();
		if (this.searchCountdown) {
			PS.alert("Wait for this countdown to finish first...", { parentElem });
			return;
		} else if (this.search.searching.includes(format)) {
			PS.alert(`You're already searching for a ${BattleLog.formatName(format)} battle...`, { parentElem });
			return;
		}
		this.searchCountdown = {
			format,
			packedTeam: team?.packedTeam || '',
			countdown: 3,
			timer: setInterval(this.doSearchCountdown, 1000),
		};
		this.update(null);
	};
	searchingFormat() {
		return this.searchCountdown?.format || this.teamSent ||
			this.search.searching?.[this.search.searching.length - 1] || null;
	}
	cancelSearch = () => {
		if (this.searchCountdown) {
			clearTimeout(this.searchCountdown.timer);
			this.searchCountdown = null;
			this.update(null);
			return true;
		}
		if (this.teamSent || this.search.searching?.length) {
			this.teamSent = null;
			PS.send(`/cancelsearch`);
			this.update(null);
			return true;
		}
		return false;
	};
	doSearchCountdown = () => {
		if (!this.searchCountdown) return; // ??? race???

		this.searchCountdown.countdown--;
		if (this.searchCountdown.countdown <= 0) {
			this.doSearch(this.searchCountdown);
			clearTimeout(this.searchCountdown.timer);
			this.searchCountdown = null;
		}
		this.update(null);
	};
	doSearch = (search: NonNullable<typeof this.searchCountdown>) => {
		this.teamSent = search.format;
		const privacy = this.adjustPrivacy();
		PS.send(`/utm ${search.packedTeam}`);
		PS.send(`${privacy}/search ${search.format}`);
	};
	override receiveLine(args: Args) {
		const [cmd] = args;
		switch (cmd) {
		case 'challstr': {
			const [, challstr] = args;
			PS.user.challstr = challstr;
			// Official Showdown re-logs-in here via a cookie-backed session on
			// Smogon's login server (PSLoginServer 'upkeep') - not applicable
			// to us, since accounts here are Supabase-backed, not Smogon's.
			// See PSUser#trySupabaseAutoLogin for our equivalent.
			PS.user.trySupabaseAutoLogin();
			return;
		} case 'updateuser': {
			const [, fullName, namedCode, avatar, settingsJSON] = args;
			const named = namedCode === '1';
			if (named) PS.user.initializing = false;
			if (settingsJSON) {
				PS.prefs.set('serversettings', { ...PS.prefs.serversettings, ...JSON.parse(settingsJSON) });
				Dex.loadTextData();
			}
			PS.user.setName(fullName, named, avatar);
			PS.teams.loadRemoteTeams();
			return;
		} case 'updatechallenges': {
			const [, challengesBuf] = args;
			this.receiveChallenges(challengesBuf);
			return;
		} case 'updatesearch': {
			const [, searchBuf] = args;
			this.receiveSearch(searchBuf);
			return;
		} case 'queryresponse': {
			const [, queryId, responseJSON] = args;
			this.handleQueryResponse(queryId as ID, JSON.parse(responseJSON));
			return;
		} case 'pm': {
			const [, user1, user2, message] = args;
			this.handlePM(user1, user2, message);
			let sideRoom = PS.rightPanel as ChatRoom;
			if (sideRoom?.type === "chat" && PS.prefs.inchatpm) sideRoom?.log?.add(args);
			return;
		} case 'customgroups': {
			const [, groupsList] = args;
			PS.server.parseGroups(groupsList);
			return;
		} case 'formats': {
			this.parseFormats(args);
			return;
		} case 'popup': {
			let [, message] = args;
			for (const roomid in PS.rooms) {
				const room = PS.rooms[roomid] as ChatRoom | MainMenuRoom;
				if (room.teamSent) {
					room.teamSent = null;
					room.update(null);
				}
				if (room.type === 'team') (room as any).cancelUpload();
			}
			let width: number | undefined;
			if (message.startsWith('|wide|')) {
				message = message.slice(6);
				width = 960;
			}
			PS.alert(message.replace(/\|\|/g, '\n'), { width });
			return;
		}
		}
		const lobby = PS.rooms['lobby'];
		if (lobby) lobby.receiveLine(args);
	}
	receiveChallenges(dataBuf: string) {
		let json;
		try {
			json = JSON.parse(dataBuf);
		} catch {}
		for (const userid in json.challengesFrom) {
			PS.getPMRoom(toID(userid));
		}
		if (json.challengeTo) {
			PS.getPMRoom(toID(json.challengeTo.to));
		}
		for (const roomid in PS.rooms) {
			const room = PS.rooms[roomid] as ChatRoom;
			if (!room.pmTarget) continue;
			const targetUserid = toID(room.pmTarget);
			if (!room.challenged && !(targetUserid in json.challengesFrom) &&
				!room.challenging && json.challengeTo?.to !== targetUserid) {
				continue;
			}
			room.challenged = room.parseChallenge(json.challengesFrom[targetUserid]);
			room.challenging = json.challengeTo?.to === targetUserid ? room.parseChallenge(json.challengeTo.format) : null;
			room.update(null);
		}
	}
	receiveSearch(dataBuf: string) {
		let json;
		this.teamSent = null;
		try {
			json = JSON.parse(dataBuf);
		} catch {}
		this.search = json;
		this.update(null);
	}
	parseFormats(formatsList: string[]) {
		let isSection = false;
		let section = '';

		let column = 0;

		window.NonBattleGames = { rps: 'Rock Paper Scissors' };
		for (let i = 3; i <= 9; i += 2) {
			window.NonBattleGames[`bestof${i}`] = `Best-of-${i}`;
		}
		window.BattleFormats = {};
		for (let j = 1; j < formatsList.length; j++) {
			const entry = formatsList[j];
			if (isSection) {
				section = entry;
				isSection = false;
			} else if (entry === ',LL') {
				PS.teams.usesLocalLadder = true;
			} else if (entry === '' || (entry.startsWith(',') && !isNaN(Number(entry.slice(1))))) {
				isSection = true;

				if (entry) {
					column = parseInt(entry.slice(1), 10) || 0;
				}
			} else {
				let name = entry;
				let searchShow = true;
				let challengeShow = true;
				let tournamentShow = true;
				let partner = false;
				let bestOfDefault = false;
				let teraPreviewDefault = false;
				let itemClauseDefault = false;
				let team: 'preset' | null = null;
				let teambuilderLevel: number | null = null;
				let lastCommaIndex = name.lastIndexOf(',');
				let code = lastCommaIndex >= 0 ? parseInt(name.substr(lastCommaIndex + 1), 16) : NaN;
				if (!isNaN(code)) {
					name = name.substr(0, lastCommaIndex);
					if (code & 1) team = 'preset';
					if (!(code & 2)) searchShow = false;
					if (!(code & 4)) challengeShow = false;
					if (!(code & 8)) tournamentShow = false;
					if (code & 16) teambuilderLevel = 50;
					if (code & 32) partner = true;
					if (code & 64) bestOfDefault = true;
					if (code & 128) teraPreviewDefault = true;
					if (code & 256) itemClauseDefault = true;
				} else {
					// Backwards compatibility: late 0.9.0 -> 0.10.0
					if (name.substr(name.length - 2) === ',#') { // preset teams
						team = 'preset';
						name = name.substr(0, name.length - 2);
					}
					if (name.substr(name.length - 2) === ',,') { // search-only
						challengeShow = false;
						name = name.substr(0, name.length - 2);
					} else if (name.substr(name.length - 1) === ',') { // challenge-only
						searchShow = false;
						name = name.substr(0, name.length - 1);
					}
				}
				let id = toID(name);
				let isTeambuilderFormat = !team && !name.endsWith('Custom Game');
				let teambuilderFormat = '' as ID;
				let teambuilderFormatName = '';
				if (isTeambuilderFormat) {
					teambuilderFormatName = name;
					if (!id.startsWith('gen')) {
						teambuilderFormatName = '[Gen 6] ' + name;
					}
					let parenPos = teambuilderFormatName.indexOf('(');
					if (parenPos > 0 && name.endsWith(')')) {
						// variation of existing tier
						teambuilderFormatName = teambuilderFormatName.slice(0, parenPos).trim();
					}
					if (teambuilderFormatName !== name) {
						teambuilderFormat = toID(teambuilderFormatName);
						if (BattleFormats[teambuilderFormat]) {
							BattleFormats[teambuilderFormat].isTeambuilderFormat = true;
						} else {
							BattleFormats[teambuilderFormat] = {
								id: teambuilderFormat,
								name: teambuilderFormatName,
								team,
								section,
								column,
								rated: false,
								isTeambuilderFormat: true,
								effectType: 'Format',
							};
						}
						isTeambuilderFormat = false;
					}
				}
				if (BattleFormats[id]?.isTeambuilderFormat) {
					isTeambuilderFormat = true;
				}
				// make sure formats aren't out-of-order
				if (BattleFormats[id]) delete BattleFormats[id];
				BattleFormats[id] = {
					id,
					name,
					team,
					section,
					column,
					searchShow,
					challengeShow,
					tournamentShow,
					bestOfDefault,
					teraPreviewDefault,
					itemClauseDefault,
					rated: searchShow && id.substr(4, 7) !== 'unrated',
					teambuilderLevel,
					partner,
					teambuilderFormat,
					isTeambuilderFormat,
					effectType: 'Format',
				};
			}
		}

		// Match base formats to their variants, if they are unavailable in the server.
		let multivariantFormats: { [id: string]: 1 } = {};
		for (let id in BattleFormats) {
			let teambuilderFormat = BattleFormats[BattleFormats[id].teambuilderFormat!];
			if (!teambuilderFormat || multivariantFormats[teambuilderFormat.id]) continue;
			if (!teambuilderFormat.searchShow && !teambuilderFormat.challengeShow && !teambuilderFormat.tournamentShow) {
				// The base format is not available.
				if (teambuilderFormat.battleFormat) {
					multivariantFormats[teambuilderFormat.id] = 1;
					teambuilderFormat.battleFormat = '';
				} else {
					teambuilderFormat.battleFormat = id;
				}
			}
		}
		PS.teams.update('format');
	}
	handlePM(user1: string, user2: string, message?: string) {
		const userid1 = toID(user1);
		const userid2 = toID(user2);
		const pmTarget = PS.user.userid === userid1 ? user2 : user1;
		const pmTargetid = PS.user.userid === userid1 ? userid2 : userid1;
		let roomid = `dm-${pmTargetid}` as RoomID;
		if (pmTargetid === PS.user.userid) roomid = 'dm-' as RoomID;
		let room = PS.rooms[roomid] as ChatRoom | undefined;
		if (!room) {
			PS.addRoom({
				id: roomid,
				args: { pmTarget },
				autofocus: false,
			});
			room = PS.rooms[roomid] as ChatRoom;
		} else {
			room.updateTarget(pmTarget);
		}
		if (message) room.receiveLine([`c`, user1, message]);
		PS.update();
	}
	/**
	 * Client-to-server query. Handles `/crq` aka `/cmd`.
	 *
	 * Most queries are still handled hardcoded, so this is only for certain
	 * special queries that need a Promise.
	 */
	makeQuery(id: string, param?: string, excludeParamFromListener?: boolean) {
		let fullid = id;
		if (param && !excludeParamFromListener) fullid += ` ${toID(param)}`;
		return new Promise<any>(resolve => {
			if (!this.listeners[fullid]) {
				this.listeners[fullid] = [];
				PS.send(`/cmd ${id} ${param || ''}`);
			}
			this.listeners[fullid]!.push(resolve);
		});
	}
	handleQueryResponse(id: ID, response: any) {
		let fullid: string = id;
		switch (id) {
		case 'userdetails':
			let userid = response.userid;
			fullid += ` ${userid}`;
			let userdetails = this.userdetailsCache[userid];
			if (!userdetails) {
				this.userdetailsCache[userid] = response;
			} else {
				response.status ||= '';
				Object.assign(userdetails, response);
			}
			if (userid === PS.user.userid) {
				if (response.avatar !== undefined && PS.user.avatar !== `${response.avatar}`) {
					PS.user.avatar = `${response.avatar}`;
					PS.user.update(null);
				}
			}
			PS.rooms[`user-${userid}`]?.update(null);
			PS.rooms[`viewuser-${userid}`]?.update(null);
			PS.rooms[`users`]?.update(null);
			break;
		case 'rooms':
			if (response.pspl) {
				for (const roomInfo of response.pspl) roomInfo.spotlight = "Spotlight";
				response.chat = [...response.pspl, ...response.chat];
				response.pspl = null;
			}
			if (response.official) {
				for (const roomInfo of response.official) roomInfo.section = "Official";
				response.chat = [...response.official, ...response.chat];
				response.official = null;
			}
			this.roomsCache = response;
			const roomsRoom = PS.rooms[`rooms`] as RoomsRoom;
			if (roomsRoom) roomsRoom.update(null);
			break;
		case 'onlinetrainers':
			this.trainersCache = response?.trainers || [];
			this.update(null);
			PS.rooms[`rooms`]?.update(null);
			PS.rooms[`battles`]?.update(null);
			break;
		case 'friendsdata':
			this.friendsCache = response || { enabled: false, friends: [], received: [], sent: [] };
			for (const roomid in PS.rooms) {
				if (roomid.startsWith('view-friends')) PS.rooms[roomid]!.update(null);
			}
			break;
		case 'roomlist':
			const battlesRoom = PS.rooms[`battles`] as BattlesRoom;
			if (battlesRoom) {
				const battleTable = response.rooms;
				const battles = [];
				for (const battleid in battleTable) {
					battleTable[battleid].id = battleid;
					battles.push(battleTable[battleid]);
				}
				battlesRoom.battles = battles;
				battlesRoom.update(null);
			}
			break;
		case 'laddertop':
			for (const [roomid, ladderRoom] of Object.entries(PS.rooms)) {
				if (roomid.startsWith('ladder-')) {
					(ladderRoom as LadderFormatRoom).update(response);
				}
			}
			break;
		case 'teamupload':
			if (PS.teams.uploading) {
				const team = PS.teams.uploading;
				team.teamid = response.teamid;
				team.uploaded = {
					teamid: response.teamid,
					notLoaded: false,
					private: response.private,
				};
				PS.rooms[`team-${team.key}`]?.update(null);
				PS.rooms.teambuilder?.update(null);
				PS.teams.uploading = null;
			}
			break;
		case 'teamupdate':
			for (const team of PS.teams.list) {
				if (team.teamid === response.teamid) {
					team.uploaded = {
						teamid: response.teamid,
						notLoaded: false,
						private: response.private,
					};
					PS.rooms[`team-${team.key}`]?.update(null);
					PS.rooms.teambuilder?.update(null);
					PS.teams.uploading = null;
					break;
				}
			}
			break;
		}
		for (const callback of this.listeners[fullid] || []) callback(response);
		delete this.listeners[fullid];
	}
}

class NewsPanel extends PSRoomPanel {
	static readonly id = 'news';
	static readonly routes = ['news'];
	static readonly title = 'News';
	static readonly location = 'mini-window';
	override render() {
		return <PSPanelWrapper room={this.props.room} fullSize>
			<div class="readable-bg" dangerouslySetInnerHTML={{ __html: PS.newsHTML }}></div>
		</PSPanelWrapper>;
	}
}

class MainMenuPanel extends PSRoomPanel<MainMenuRoom> {
	static readonly id = 'mainmenu';
	static readonly routes = [''];
	static readonly Model = MainMenuRoom;
	static readonly icon = <i class="fa fa-home" aria-hidden></i>;
	override componentDidMount() {
		super.componentDidMount();
		this.subscribeTo(PSBackground);
		this.subscribeTo(PS.user, () => PS.mainmenu.requestTrainers());
		PS.mainmenu.watchTrainers();
	}
	challengeOpen = false;
	/** Console mode: the home tiles folding away before the Battle screen. */
	battleFolding = false;
	toggleChallenge = (e: Event) => {
		e.preventDefault();
		if (consoleModeOn()) return this.toggleConsoleBattle();
		this.challengeOpen = !this.challengeOpen;
		if (this.challengeOpen) PS.mainmenu.requestTrainers();
		this.forceUpdate();
	};
	/**
	 * Console mode's Battle!: the tiles and dock fold away, then the Battle
	 * screen takes the whole home (renderConsoleBattle). Back unfolds them.
	 */
	toggleConsoleBattle() {
		if (this.battleFolding) return;
		const opening = !this.challengeOpen;
		const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		const finish = () => {
			this.battleFolding = false;
			this.challengeOpen = opening;
			this.forceUpdate();
			setTimeout(() => {
				if (opening) {
					PSConsole.focusIn(this.base?.querySelector<HTMLElement>('.kt-battle-menu'));
				} else {
					const battle = this.base?.querySelector<HTMLElement>('.battle-launcher .big.button');
					if (battle) PSConsole.focus(battle);
				}
			}, 40);
		};
		if (opening) {
			PS.mainmenu.requestTrainers();
			if (still) return finish();
			this.battleFolding = true;
			this.forceUpdate();
			setTimeout(finish, 520);
		} else {
			finish();
		}
	}
	closeConsoleBattle = (e: Event) => {
		e.preventDefault();
		if (this.challengeOpen) this.toggleConsoleBattle();
	};
	/** The Battle screen: who's online, as big challenge cards, and your tournament match. */
	renderConsoleBattle() {
		const trainers = (PS.mainmenu.trainersCache || []).filter(t => t.userid !== PS.user.userid);
		const ready = trainers.filter(t => !t.busy && !t.battles.length).length;
		return <div class="menugroup battle-launcher kt-battle is-open">
			<section class="kt-battle-menu" aria-label="Battle">
				<header class="kt-battle-head">
					<span class="kt-battle-burst" aria-hidden></span>
					<h2 class="kt-battle-title">Battle!</h2>
					<p class="kt-battle-sub">
						{PS.mainmenu.trainersCache === null ? 'Looking for trainers...' :
							!trainers.length ? 'No other trainers are online right now.' :
							ready === 1 ? '1 trainer is ready to battle.' : `${ready} trainers are ready to battle.`}
					</p>
					<button class="button kt-battle-back" data-console-back onClick={this.closeConsoleBattle}>
						<i class="fa fa-chevron-left" aria-hidden></i> Back
					</button>
				</header>
				<div class="kt-battle-trainers" role="list">
					{trainers.map((t, i) => <button
						class={`kt-battle-trainer${t.battles.length ? ' is-battling' : ''}${t.busy ? ' is-busy' : ''}`}
						data-href={`challenge-${t.userid}`} disabled={t.busy} role="listitem"
						style={`--kt-i:${i}`} data-console-default={i === 0 ? true : undefined}
					>
						<span class="kt-battle-initial" aria-hidden>{t.name.charAt(0).toUpperCase()}</span>
						<span class="kt-battle-name">
							<strong>{t.name}</strong>
							<small>{t.busy ? 'Not taking challenges' : t.battles.length ? 'Battling now' : 'Ready'}</small>
						</span>
						<span class="kt-battle-go" aria-hidden>{t.busy ? '' : 'VS'}</span>
					</button>)}
				</div>
			</section>
		</div>;
	}
	/**
	 * Battle! opens the trainers online, to challenge one directly: this is a
	 * private campaign server, so there is no random matchmaking.
	 */
	renderChallengeButton() {
		if (consoleModeOn() && this.challengeOpen) return this.renderConsoleBattle();
		const trainers = (PS.mainmenu.trainersCache || []).filter(t => t.userid !== PS.user.userid);
		return <div class={`menugroup battle-launcher${this.challengeOpen ? ' is-open' : ''}`}>
			<button
				class="mainmenu1 mainmenu big button" onClick={this.toggleChallenge}
				aria-expanded={this.challengeOpen ? 'true' : 'false'}
			>
				<strong>Battle!</strong><br />
				<small>Challenge a trainer <i class={`fa fa-caret-${this.challengeOpen ? 'up' : 'down'}`} aria-hidden></i></small>
			</button>
			{this.challengeOpen && !consoleModeOn() && <div class="challenge-list" role="list">
				{PS.mainmenu.trainersCache === null ? (
					<p class="challenge-empty"><em>Looking for trainers...</em></p>
				) : !trainers.length ? (
					<p class="challenge-empty"><em>No other trainers are online right now.</em></p>
				) : trainers.map(t => <button
					class={`challenge-trainer${t.battles.length ? ' is-battling' : ''}${t.busy ? ' is-busy' : ''}`}
					data-href={`challenge-${t.userid}`} disabled={t.busy} role="listitem"
				>
					<strong>{t.name}</strong>
					<small>{t.busy ? 'Not taking challenges' : t.battles.length ? 'Battling now' : 'Ready'}</small>
				</button>)}
			</div>}
		</div>;
	}
	override focus(options?: PSRoomFocusOptions) {
		if (!options?.preventScroll) PSView.scrollToRoom();
		if (PSView.hasTapped) return;

		PSView.politeFocus(this.base?.querySelector<HTMLButtonElement>('.formatselect'));
	}
	submitSearch = (ev: Event, format: string, team?: Team) => {
		if (!PS.user.named) {
			PS.join('login' as RoomID, {
				parentElem: this.base!.querySelector<HTMLElement>('.big.button'),
			});
			return;
		}
		PS.mainmenu.startSearch(format, team, ev.target as HTMLElement);
	};
	handleDragStart = (e: DragEvent) => {
		const room = PS.getRoom(e.currentTarget);
		if (!room) return;
		const foreground = (PS.leftPanel.id === room.id || PS.rightPanel?.id === room.id);
		PS.dragging = { type: 'room', roomid: room.id, foreground };
	};
	handleDragEnter = (e: DragEvent) => {
		// console.log('dragenter ' + e.dataTransfer!.dropEffect);
		e.preventDefault();
		if (PS.dragging?.type !== 'room') return;
		const draggingRoom = PS.dragging.roomid;
		if (draggingRoom === null) return;

		const draggedOverRoom = PS.getRoom(e.target);
		if (draggingRoom === draggedOverRoom?.id) return;

		const index = PS.miniRoomList.indexOf(draggedOverRoom?.id as any);
		if (index >= 0) {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', index);
		} else if (PS.rooms[draggingRoom]?.location !== 'mini-window') {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', 0);
		}

		// dropEffect !== 'none' prevents bounce-back animation in
		// Chrome/Safari/Opera
		// if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
	};
	renderMiniRoom(room: PSRoom) {
		return <PSPanelErrorBoundary key={room.id} room={room} />;
	}
	handleClickMinimize = (e: MouseEvent) => {
		if ((e.target as Element)?.getAttribute('data-cmd')) {
			return;
		}
		if (((e.target as Element)?.parentNode as Element)?.getAttribute('data-cmd')) {
			return;
		}
		const room = PS.getRoom(e.currentTarget);
		if (room) {
			room.minimized = !room.minimized;
			this.forceUpdate();
		}
	};
	/**
	 * The title bar of a chat window in the interface themes: who it is with,
	 * whether they are online or battling (from the trainers list), and the
	 * window controls in the usual order.
	 */
	renderMiniHeader(room: PSRoom) {
		const pmTarget = (room as ChatRoom).pmTarget;
		const name = pmTarget || room.title;
		const trainer = pmTarget ? PS.mainmenu.trainersCache?.find(t => t.userid === toID(pmTarget)) : undefined;
		const status = !pmTarget ? '' : !PS.mainmenu.trainersCache ? '' :
			!trainer ? 'Offline' : trainer.battles.length ? 'Battling now' : trainer.busy ? 'Busy' : 'Online';
		const statusKey = status.split(' ')[0].toLowerCase();
		return <>
			<span class="mw-avatar" aria-hidden>{(name.replace(/^\W+/, '')[0] || '?').toUpperCase()}</span>
			<span class="mw-title">
				<strong>{name}</strong>
				{status && <small class={`mw-status is-${statusKey}`}>{status}</small>}
			</span>
			<span class="mw-controls">
				<button class="minimizebutton" tabIndex={-1} aria-label={room.minimized ? 'Expand' : 'Collapse'}>
					<i class={`fa fa-${room.minimized ? 'chevron-down' : 'minus'}`} aria-hidden></i>
				</button>
				<button class="maximizebutton" data-cmd="/maximize" tabIndex={-1} aria-label="Open in a full panel">
					<i class="fa fa-expand" aria-hidden></i>
				</button>
				<button class="closebutton" data-cmd="/close" aria-label="Close" tabIndex={-1}>
					<i class="fa fa-times" aria-hidden></i>
				</button>
			</span>
		</>;
	}
	renderMiniRooms() {
		return PS.miniRoomList.map(roomid => {
			const room = PS.rooms[roomid]!;
			const notifying = room.notifications.length ? ' notifying' : room.isSubtleNotifying ? ' subtle-notifying' : '';
			return <div
				class={`mini-window${room.minimized ? ' collapsed' : ''}${room === PS.room ? ' focused' : ''}`}
				key={roomid} data-roomid={roomid}
			>
				<h3
					class={`mini-window-header${notifying}`} draggable onDragStart={this.handleDragStart} onClick={this.handleClickMinimize}
				>
					{interfaceTheme() === 'classic' ? <>
						<button class="closebutton" data-cmd="/close" aria-label="Close" tabIndex={-1}>
							<i class="fa fa-times-circle" aria-hidden></i>
						</button>
						<button class="maximizebutton" data-cmd="/maximize" tabIndex={-1} aria-label="Maximize">
							<i class="fa fa-stop-circle" aria-hidden></i>
						</button>
						<button class="minimizebutton" tabIndex={-1} aria-label="Expand/Collapse">
							<i class="fa fa-minus-circle" aria-hidden></i>
						</button>
						{room.title}
					</> : this.renderMiniHeader(room)}
				</h3>
				{this.renderMiniRoom(room)}
			</div>;
		});
	}
	renderGames() {
		if (!PS.mainmenu.search.games) return null;

		// This does not use the word "game" because it includes things like help tickets
		return <div class="menugroup">
			<p class="label">You are in:</p>
			{Object.entries(PS.mainmenu.search.games).map(([roomid, gameName]) => <div>
				<a class="blocklink" href={`${roomid}`}>{gameName}</a>
			</div>)}
		</div>;
	}
	renderSearchButton() {
		if (PS.down) {
			return <div class="menugroup" style="background: rgba(10,10,10,.6)">
				{PS.down === 'ddos' ? (
					<p class="error"><strong>Pok&eacute;mon Showdown is offline due to a DDoS attack!</strong></p>
				) : (
					<p class="error"><strong>Pok&eacute;mon Showdown is offline due to technical difficulties!</strong></p>
				)}
				<p>
					<div style={{ textAlign: 'center' }}>
						<img width="96" height="96" src={`//${Config.routes.client}/sprites/gen5/teddiursa.png`} alt="" />
					</div>
					Bear with us as we freak out.
				</p>
				<p>(We'll be back up in a few hours.)</p>
			</div>;
		}

		if (!PS.user.userid || PS.isOffline) {
			return <TeamForm class="menugroup" onSubmit={this.submitSearch} selectType="search">
				<button class="mainmenu1 mainmenu big button disabled" disabled name="search">
					<em>{PS.isOffline ? [<span class="fa-stack fa-lg">
						<i class="fa fa-plug fa-flip-horizontal fa-stack-1x" aria-hidden></i>
						<i class="fa fa-ban fa-stack-2x text-danger" aria-hidden></i>
					</span>, " Disconnected"] : "Connecting..."}</em>
				</button>
				{PS.isOffline && <p class="buttonbar">
					<button class="button" data-cmd="/reconnect">
						<i class="fa fa-plug" aria-hidden></i> <strong>Reconnect</strong>
					</button> {}
					<ReconnectTimer />
				</p>}
			</TeamForm>;
		}

		return <TeamForm
			class="menugroup" format={PS.mainmenu.searchingFormat() || undefined}
			selectType="search" onSubmit={this.submitSearch}
		>
			<p>
				<button class="button small" data-href="battleoptions" title="Options" aria-label="Options">
					Battle options <i class="fa fa-caret-down"></i>
				</button></p>
			{PS.mainmenu.searchCountdown ? (
				<>
					<button class="mainmenu1 mainmenu big button disabled" disabled><strong>
						<i class="fa fa-refresh fa-spin" aria-hidden></i> Searching in {PS.mainmenu.searchCountdown.countdown}...
					</strong></button>
					<p class="buttonbar"><button class="button" data-cmd="/cancelsearch">Cancel</button></p>
				</>
			) : PS.mainmenu.searchingFormat() ? (
				<>
					<button class="mainmenu1 mainmenu big button disabled" disabled>
						<strong><i class="fa fa-refresh fa-spin" aria-hidden></i> Searching...</strong>
					</button>
					<p class="buttonbar"><button class="button" data-cmd="/cancelsearch">Cancel</button></p>
				</>
			) : (
				<button class="mainmenu1 mainmenu big button" type="submit">
					<strong>Battle!</strong><br />
					<small>Find a random opponent</small>
				</button>
			)}
		</TeamForm>;
	}
	/**
	 * Your match: a tournament match from the campaign that is waiting on you
	 * (or, for a GM, on one of your NPC trainers), with a one-click challenge
	 * that carries the match, so the result goes back to the bracket.
	 */
	challengeMatch = (ev: Event) => {
		ev.preventDefault();
		PSTabletop.challengeMatch(Number((ev.currentTarget as HTMLButtonElement).value));
	};
	renderYourMatches() {
		// Console mode marks the Tournaments tile instead (renderConsoleMenu).
		if (consoleModeOn()) return null;
		const matches = PSTabletop.myMatches;
		if (!matches.length || !PS.user.named) return null;
		const online = new Set((PS.mainmenu.trainersCache || []).map(t => t.userid));
		return <div class="menugroup your-matches">
			<h2 class="your-matches-label"><i class="fa fa-trophy" aria-hidden></i> Your {matches.length > 1 ? 'matches' : 'match'}</h2>
			{matches.map(m => {
				const opponent = toID(m.opponent.showdown);
				const self = opponent === PS.user.userid;
				const isOnline = online.has(opponent);
				return <div class="your-match">
					<span class="your-match-info">
						<small>{m.tournament} &middot; {m.label}</small>
						<strong>{m.me.npc ? `${m.me.name} vs. ${m.opponent.name}` : `vs. ${m.opponent.name}`}</strong>
						<small class={`your-match-status${isOnline ? ' is-online' : ''}`}>
							{self ? 'Both sides are yours: set the result on the GM Desk' :
								isOnline ? (m.opponent.npc ? `Played by ${m.opponent.showdown}, online now` : 'Online now') :
								`Waiting for ${m.opponent.npc ? m.opponent.showdown : m.opponent.name} to come online`}
						</small>
					</span>
					<button
						class="button your-match-go" value={m.match_id} onClick={this.challengeMatch}
						disabled={self || !isOnline}
					><strong>Challenge</strong></button>
				</div>;
			})}
			<p class="your-matches-more"><a href="tournaments">See the bracket</a></p>
		</div>;
	}
	/** Battle!, or a search already running (from before random matchmaking was removed). */
	renderBattleSection() {
		if (PS.down || !PS.user.userid || PS.isOffline) return this.renderSearchButton();
		if (PS.mainmenu.searchCountdown || PS.mainmenu.searchingFormat()) return this.renderSearchButton();
		return this.renderChallengeButton();
	}
	renderBackgroundCredit() {
		const attrib = PSBackground.attrib;
		if (!attrib) return null;
		return (
			<small>
				<a href={attrib.url} target="_blank" class="subtle">"{attrib.title}" <small>background by {attrib.artist}</small></a>
			</small>
		);
	}
	/** Galar's menu: tiles that say what each place is for, grouped by purpose. */
	renderGalarMenu(onlineButton: string) {
		const tile = (href: string, cls: string, icon: string, title: string, desc: string) => <p>
			<a class={`gx-tile ${cls} mainmenu${cls === 'mainmenu2' ? ' button' : onlineButton}`} href={href}>
				<i class={`fa fa-${icon}`} aria-hidden></i>
				<span class="gx-tile-text"><strong>{title}</strong><small>{desc}</small></span>
			</a>
		</p>;
		return <>
			<div class="menugroup gx-menu">
				<h2 class="gx-menu-label">Get ready</h2>
				{tile('teambuilder', 'mainmenu2', 'pencil-square-o', 'Teambuilder', 'Build, edit and save your teams')}
				{tile('tournaments', 'mainmenu4', 'sitemap', 'Tournaments', 'Brackets from your campaign')}
			</div>
			<div class="menugroup gx-menu">
				<h2 class="gx-menu-label">Explore</h2>
				{tile('battles', 'mainmenu4', 'eye', 'Watch a battle', 'Spectate battles happening now')}
				{tile('users', 'mainmenu5', 'search', 'Find a user', 'Look up a player by name')}
				{tile('view-friends-all', 'mainmenu6', 'users', 'Friends', 'See which friends are online')}
			</div>
		</>;
	}
	/** Kanto's menu: a console home screen of big tiles, then a dock of round icons. */
	/** Console mode's home (Kanto, and any theme with Console mode on): big tiles and a dock. */
	renderConsoleMenu(onlineButton: string) {
		// A ! on a tile that has something for you: a tournament match to play,
		// a battle on to watch.
		const tile = (href: string, cls: string, icon: string, title: string, alert = '') => <a
			class={`kt-tile ${cls} mainmenu${cls === 'mainmenu2' ? ' button' : onlineButton}${alert ? ' has-alert' : ''}`} href={href}
		>
			<i class={`fa fa-${icon}`} aria-hidden></i>
			<span>{title}</span>
			{alert && <span class="kt-alert" title={alert}><span aria-hidden>!</span><span class="pkc-sr">{alert}</span></span>}
		</a>;
		const matches = PSTabletop.myMatches.length;
		const battles = consoleBattlesOn();
		const round = (href: string, cls: string, icon: string, title: string, offline?: boolean) => <a
			class={`kt-round ${cls} mainmenu${offline ? ' button' : onlineButton}`} href={href}
		>
			<span class="kt-round-icon"><i class={`fa fa-${icon}`} aria-hidden></i></span>
			<span>{title}</span>
		</a>;
		return <div class="menugroup kt-home">
			<div class="kt-tiles">
				{tile('teambuilder', 'mainmenu2', 'pencil-square-o', 'Teambuilder')}
				{tile('tournaments', 'mainmenu4', 'trophy', 'Tournaments',
					matches ? (matches === 1 ? 'You have a match to play' : `You have ${matches} matches to play`) : '')}
				{tile('battles', 'mainmenu4', 'eye', 'Watch',
					battles ? (battles === 1 ? 'A battle is on' : `${battles} battles are on`) : '')}
				{tile('view-friends-all', 'mainmenu6', 'users', 'Friends')}
			</div>
			<div class="kt-dock">
				{round('users', 'mainmenu5', 'search', 'Find a user')}
				{round('options', 'mainmenu7', 'cog', 'Options', true)}
			</div>
		</div>;
	}
	/** Paldea's menu: slanted ribbon bars with hexagonal Tera gems. */
	renderPaldeaMenu(onlineButton: string) {
		const bar = (href: string, cls: string, icon: string, title: string) => <a
			class={`pd-bar ${cls} mainmenu${cls === 'mainmenu2' ? ' button' : onlineButton}`} href={href}
		>
			<span class="pd-bar-bg" aria-hidden></span>
			<span class="pd-gem"><i class={`fa fa-${icon}`} aria-hidden></i></span>
			<span class="pd-bar-text">{title}</span>
			<i class="fa fa-chevron-right pd-bar-go" aria-hidden></i>
		</a>;
		return <>
			<div class="menugroup pd-menu">
				<h2 class="pd-menu-label">Battle</h2>
				{bar('teambuilder', 'mainmenu2', 'pencil-square-o', 'Teambuilder')}
				{bar('tournaments', 'mainmenu4', 'trophy', 'Tournaments')}
				{bar('battles', 'mainmenu4', 'eye', 'Watch a battle')}
			</div>
			<div class="menugroup pd-menu">
				<h2 class="pd-menu-label">Friends</h2>
				{bar('users', 'mainmenu5', 'search', 'Find a user')}
				{bar('view-friends-all', 'mainmenu6', 'users', 'Friends')}
			</div>
		</>;
	}
	/** Alola's menu: a Rotom Dex screen of round app icons. */
	renderAlolaMenu(onlineButton: string) {
		const app = (href: string, cls: string, icon: string, title: string) => <a
			class={`al-app ${cls} mainmenu${cls === 'mainmenu2' ? ' button' : onlineButton}`} href={href}
		>
			<span class="al-app-icon"><i class={`fa fa-${icon}`} aria-hidden></i></span>
			<span>{title}</span>
		</a>;
		return <>
			<div class="menugroup al-menu">
				<h2 class="al-menu-label">Battle</h2>
				<div class="al-grid">
					{app('teambuilder', 'mainmenu2', 'pencil-square-o', 'Teambuilder')}
					{app('tournaments', 'mainmenu4 al-app-tour', 'trophy', 'Tournaments')}
					{app('battles', 'mainmenu4 al-app-watch', 'eye', 'Watch')}
				</div>
			</div>
			<div class="menugroup al-menu">
				<h2 class="al-menu-label">Friends</h2>
				<div class="al-grid">
					{app('users', 'mainmenu5', 'search', 'Find a user')}
					{app('view-friends-all', 'mainmenu6', 'users', 'Friends')}
				</div>
			</div>
		</>;
	}
	/** Unova's menu: a C-Gear grid of icon tiles. */
	renderUnovaMenu(onlineButton: string) {
		const tile = (href: string, cls: string, icon: string, title: string) => <a
			class={`ux-tile ${cls} mainmenu${cls === 'mainmenu2' ? ' button' : onlineButton}`} href={href}
		>
			<i class={`fa fa-${icon}`} aria-hidden></i>
			<span>{title}</span>
		</a>;
		return <>
			<div class="menugroup ux-menu">
				<h2 class="ux-menu-label">Battle</h2>
				<div class="ux-grid">
					{tile('teambuilder', 'mainmenu2', 'pencil-square-o', 'Teambuilder')}
					{tile('tournaments', 'mainmenu4', 'sitemap', 'Tournaments')}
					{tile('battles', 'mainmenu4', 'eye', 'Watch')}
				</div>
			</div>
			<div class="menugroup ux-menu">
				<h2 class="ux-menu-label">Link</h2>
				<div class="ux-grid">
					{tile('users', 'mainmenu5', 'search', 'Find a user')}
					{tile('view-friends-all', 'mainmenu6', 'users', 'Friends')}
				</div>
			</div>
		</>;
	}
	override render() {
		const onlineButton = ' button' + (PS.isOffline ? ' disabled' : '');
		const tinyLayout = this.props.room.width < 620 ? ' tiny-layout' : '';
		return <PSPanelWrapper room={this.props.room} onDragEnter={this.handleDragEnter}>
			<div class={`mainmenu-mini-windows${tinyLayout}`}>
				{!PS.leftPanelWidth && Config.includes?.mainmenuHTML && (
					<div dangerouslySetInnerHTML={{ __html: Config.includes.mainmenuHTML }} />
				)}
				{this.renderMiniRooms()}
			</div>
			<div class={`mainmenu${tinyLayout}`}>
				<div class={`mainmenu-left${!consoleModeOn() ? '' : this.challengeOpen ? ' kt-battle-open' :
					this.battleFolding ? ' kt-battle-folding' : ''}`}>
					{this.renderGames()}

					{this.renderBattleSection()}

					{this.renderYourMatches()}

					{consoleModeOn() ? this.renderConsoleMenu(onlineButton) :
					interfaceTheme() === 'galar' ? this.renderGalarMenu(onlineButton) :
						interfaceTheme() === 'unova' ? this.renderUnovaMenu(onlineButton) :
						interfaceTheme() === 'alola' ? this.renderAlolaMenu(onlineButton) :
						interfaceTheme() === 'paldea' ? this.renderPaldeaMenu(onlineButton) :
						<>
						<div class="menugroup">
							<p><a class="mainmenu2 mainmenu button" href="teambuilder">Teambuilder</a></p>
							<p><a class={"mainmenu4 mainmenu" + onlineButton} href="tournaments">Tournaments</a></p>
						</div>

						<div class="menugroup">
							<p><a class={"mainmenu4 mainmenu" + onlineButton} href="battles">Watch a battle</a></p>
							<p><a class={"mainmenu5 mainmenu" + onlineButton} href="users">Find a user</a></p>
							<p><a class={"mainmenu6 mainmenu" + onlineButton} href="view-friends-all">Friends</a></p>
						</div>
					</>}
				</div>
				<div class="mainmenu-footer">
					<div class="bgcredit">{this.renderBackgroundCredit()}</div>
					<small>
						<a href={`//${Config.routes.dex}/`} target="_blank">Pok&eacute;dex</a> | {}
						<a href={`//${Config.routes.replays}/`} target="_blank">Replays</a> | {}
						<a href="/fresh" class="no-panel-intercept" title="Reload every client file without using the browser cache">Refresh client</a>
					</small>
					<CCPAIntercept />
				</div>
			</div>
		</PSPanelWrapper>;
	}
}

export class CCPAIntercept extends preact.Component {
	intercepted = false;
	override shouldComponentUpdate() {
		this.intercept();
		return false;
	}
	override componentDidMount() {
		this.intercept();
		setTimeout(() => this.intercept(), 500);
		setTimeout(() => this.intercept(), 1000);
		setTimeout(() => this.intercept(), 2000);
		setTimeout(() => this.intercept(), 3000);
		setTimeout(() => this.intercept(), 5000);
		setTimeout(() => this.intercept(), 10000);
	}
	intercept() {
		if (this.intercepted || !window.$) return;
		const $ccpa = $('.fc-dns-dialog');
		if (!$ccpa.length) return;
		$ccpa.appendTo(this.base!);
		// $ccpa.css({ position: 'relative', zIndex: 2 });
		this.intercepted = true;
	}
	override render() {
		return <div></div>;
	}
}

/** One trainer online, from /cmd onlinetrainers (server/chat-plugins/campaign-home.ts). */
export interface OnlineTrainer {
	userid: ID;
	name: string;
	avatar: string | number;
	status: string;
	busy: boolean;
	battles: { id: RoomID, title: string, p1: string, p2: string }[];
}

/** How many battles are on, by the trainers online (each battle once). */
function consoleBattlesOn() {
	const ids = new Set<string>();
	for (const trainer of PS.mainmenu.trainersCache || []) {
		for (const battle of trainer.battles) ids.add(battle.id);
	}
	return ids.size;
}

/** A friend, from /cmd friendsdata (server/chat-plugins/campaign-home.ts). */
export interface ConsoleFriend {
	userid: ID;
	name: string;
	online: boolean;
	status: string;
	message: string;
	lastSeen: number | null;
	busy: boolean;
	battles: { id: RoomID, title: string, p1: string, p2: string }[];
}
export interface ConsoleFriendsData {
	enabled: boolean;
	friends: ConsoleFriend[];
	received: ID[];
	sent: ID[];
}

/**
 * The only format players can pick, when the server offers just one (our
 * campaign server does). Format pickers hide then: there is nothing to choose.
 */
export function onlyFormat(): ID | null {
	const ids = Object.keys(window.BattleFormats || {}).filter(id => window.BattleFormats[id]?.challengeShow !== false);
	return ids.length === 1 ? ids[0] as ID : null;
}

export class FormatDropdown extends preact.Component<{
	selectType?: SelectType, format?: string, defaultFormat?: string, placeholder?: string,
	onChange?: JSX.EventHandler<Event>,
}> {
	declare base?: HTMLButtonElement;
	format = '';
	change = (e: Event) => {
		if (!this.base) return;
		this.format = this.base.value;
		this.forceUpdate();
		if (this.props.onChange) this.props.onChange(e);
	};
	render() {
		this.format = this.props.format || this.format || this.props.defaultFormat || '';
		let [formatName, customRules] = this.format.split('@@@');
		customRules = customRules?.replace(/,/g, ', ');
		if (window.BattleLog) formatName = BattleLog.formatName(formatName);
		if (this.props.format && !this.props.onChange) {
			// There's intentionally no `disabled` prop. If this is out of sync
			// with the `format` and `onChange` props, that's a bug.
			return <button
				name="format" value={this.format} class="select formatselect preselected" disabled
			>
				{formatName}
				{!!customRules && [<br />, <small>Custom rules: {customRules}</small>]}
			</button>;
		}
		return <button
			name="format" value={this.format} data-selecttype={this.props.selectType}
			class="select formatselect" data-href="/formatdropdown" onChange={this.change}
		>
			{formatName || (!!this.props.placeholder && <em>{this.props.placeholder}</em>) || null}
			{!!customRules && [<br />, <small>Custom rules: {customRules}</small>]}
		</button>;
	}
}

export type MultiplayerBattleOptions = {
	multiplayerDoubles: true,
	teammate: string,
	partnerTeam: string,
};

class TeamDropdown extends preact.Component<{ format: string, onChange?: (teamKey: string) => void }> {
	teamFormat = '';
	teamKey = '';
	change = () => {
		if (!this.base) return;
		this.teamKey = (this.base as HTMLButtonElement).value;
		this.forceUpdate();
		this.props.onChange?.(this.teamKey);
	};
	getDefaultTeam(teambuilderFormat: string) {
		for (const team of PS.teams.list) {
			if (!team.isBox && team.format === teambuilderFormat) return team.key;
		}
		return '';
	}
	render() {
		const teamFormat = PS.teams.teambuilderFormat(this.props.format);
		const formatData = window.BattleFormats?.[teamFormat];
		if (formatData?.team) {
			return <button class="select teamselect preselected" name="team" value="random" disabled>
				<div class="team">
					<strong>Random team</strong>
					<small>
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
					</small>
				</div>
			</button>;
		}
		if (teamFormat !== this.teamFormat) {
			this.teamFormat = teamFormat;
			this.teamKey = this.getDefaultTeam(teamFormat);
		}
		const team = PS.teams.byKey[this.teamKey] || null;
		return <button
			name="team" value={this.teamKey}
			class="select teamselect" data-href="/teamdropdown" data-format={teamFormat} onChange={this.change}
			disabled={!!PS.mainmenu.searchingFormat()}
		>
			{PS.roomTypes['teamdropdown'] && <TeamBox team={team} noLink />}
		</button>;
	}
}

function MultiplayerTeamPicker(props: {
	team?: Team,
	selected: globalThis.Set<number>,
	limit: number,
	role?: 'primary' | 'partner',
	teammate: string,
	onChangeTeammate: (ev: Event) => void,
	onTogglePokemon: (ev: Event) => void,
}) {
	const sets = props.team?.packedTeam ? Teams.unpack(props.team.packedTeam) : [];
	return <fieldset class="fieldset multiplayer-team-picker">
		<legend>{props.role === 'partner' ? 'Your roster' : 'Doubles roster'}</legend>
		{props.role !== 'partner' && <p>
			<label>Invite teammate:<br />
				<input
					class="textbox" type="text" name="teammate" value={props.teammate}
					placeholder="Username (optional)" onInput={props.onChangeTeammate}
				/>
			</label>
		</p>}
		<div class="multiplayer-team-picker-heading">
			<strong>Pokémon</strong><small>{props.selected.size}/{props.limit}</small>
		</div>
		<div class="multiplayer-team-picker-list">
			{sets.map((set, index) => <label class="checkbox multiplayer-team-picker-mon">
				<input
					type="checkbox" name="multiplayerpokemon" value={index}
					checked={props.selected.has(index)}
					disabled={!props.selected.has(index) && props.selected.size >= props.limit}
					onChange={props.onTogglePokemon}
				/>
				<PSIcon pokemon={set} /> <span>{set.name || set.species}</span>
			</label>)}
			{!sets.length && <em>Select a team</em>}
		</div>
	</fieldset>;
}

export class TeamForm extends preact.Component<{
	children: preact.ComponentChildren,
	class?: string, format?: string, teamFormat?: string, hideFormat?: boolean, selectType?: SelectType,
	defaultFormat?: string,
	multiplayerRole?: 'primary' | 'partner',
	onSubmit: ((e: Event, format: string, team?: Team, options?: MultiplayerBattleOptions) => void) | null,
	onValidate?: ((e: Event, format: string, team?: Team, options?: MultiplayerBattleOptions) => void) | null,
}> {
	format = '';
	teraPreview = false;
	bestOf = false;
	bestOfValue = '3';
	// Defaults to expanded (rather than upstream's collapsed-by-default) since
	// this server only has one format and the 4 mechanic checkboxes below are
	// central to it.
	customRules = true;
	customRuleText = '';
	itemClause = false;
	megaEvolution = false;
	zPower = false;
	gigantamax = false;
	terastal = false;
	multiplayerDoubles = false;
	/** A Tabletop tournament match this challenge is for (its Tabletop Match rule). */
	tabletopMatch = '';
	teammate = '';
	selectedPokemon = new globalThis.Set<number>();
	selectedTeamKey = '';
	initializedTeamKey = '';
	override componentDidMount() {
		this.initializeDefaultSelection();
	}
	override componentDidUpdate(previousProps: Readonly<typeof this.props>) {
		if (previousProps.format !== this.props.format || previousProps.multiplayerRole !== this.props.multiplayerRole) {
			this.initializedTeamKey = '';
			this.selectedPokemon.clear();
		}
		this.initializeDefaultSelection();
	}
	initializeDefaultSelection() {
		if (!this.multiplayerDoubles) return;
		const team = this.getSelectedTeam();
		if (!team || team.key === this.initializedTeamKey) return;
		this.initializedTeamKey = team.key;
		PS.teams.loadTeam(team).then(() => {
			if (this.getSelectedTeam()?.key !== team.key) return;
			this.initializeSelection(team);
			this.forceUpdate();
		});
	}
	changeFormat = (ev: Event) => {
		this.setFormat((ev.target as HTMLButtonElement).value);
	};
	setFormat(format: string) {
		const [baseFormat, customRules] = format.split('@@@');
		this.format = baseFormat;
		this.loadCustomRules(customRules);
	};
	loadCustomRules(customRules: string) {
		this.bestOf = false;
		this.bestOfValue = '3';
		this.teraPreview = false;
		this.itemClause = false;
		this.megaEvolution = false;
		this.zPower = false;
		this.gigantamax = false;
		this.terastal = false;
		this.tabletopMatch = '';
		this.multiplayerDoubles = this.props.multiplayerRole === 'partner';
		if (!customRules) {
			this.customRules = true; // stay expanded - see the constructor-time comment
			this.customRuleText = '';
			return;
		}

		this.customRules = true;
		const unknownRules: string[] = [];
		for (const rule of customRules.split(',')) {
			const trimmedRule = rule.trim();
			if (!trimmedRule) continue;
			const bestOfMatch = /^best[-\s]*of\s*=\s*(\d+)$/i.exec(trimmedRule);
			if (bestOfMatch) {
				this.bestOf = true;
				this.bestOfValue = bestOfMatch[1];
			} else if (/^tera\s+type\s+preview$/i.test(trimmedRule)) {
				this.teraPreview = true;
			} else if (/^item\s+clause\s*=\s*1$/i.test(trimmedRule)) {
				this.itemClause = true;
			} else if (/^mega\s+evolution\s+rule$/i.test(trimmedRule)) {
				this.megaEvolution = true;
			} else if (/^z-?power\s+rule$/i.test(trimmedRule)) {
				this.zPower = true;
			} else if (/^gigantamax\s+rule$/i.test(trimmedRule)) {
				this.gigantamax = true;
			} else if (/^terastal\s+rule$/i.test(trimmedRule)) {
				this.terastal = true;
			} else if (/^multiplayer\s+doubles$/i.test(trimmedRule)) {
				this.multiplayerDoubles = true;
			} else if (/^tabletop\s*match\s*=\s*\d+$/i.test(trimmedRule)) {
				this.tabletopMatch = trimmedRule.replace(/\D+/g, '');
			} else {
				unknownRules.push(trimmedRule);
			}
		}
		this.customRuleText = unknownRules.join('\n');
	};
	changeBestOfValue = (ev: Event) => {
		this.bestOfValue = (ev.target as HTMLInputElement).value;
	};
	changeCustomRules = (ev: Event) => {
		this.customRuleText = (ev.target as HTMLTextAreaElement).value;
	};
	getSelectedTeam() {
		const teamElement = this.base?.querySelector<HTMLButtonElement>('button[name=team]');
		const teamKey = this.selectedTeamKey || teamElement?.value || '';
		return teamKey ? PS.teams.byKey[teamKey] : undefined;
	}
	selectionLimit() {
		return this.props.multiplayerRole === 'partner' || this.teammate.trim() ? 3 : 6;
	}
	initializeSelection(team?: Team) {
		this.initializedTeamKey = team?.key || '';
		this.selectedPokemon.clear();
		if (!team?.packedTeam) return;
		const sets = Teams.unpack(team.packedTeam);
		for (let i = 0; i < Math.min(sets.length, this.selectionLimit()); i++) {
			this.selectedPokemon.add(i);
		}
	}
	changeTeam = (teamKey: string) => {
		this.selectedTeamKey = teamKey;
		const team = teamKey ? PS.teams.byKey[teamKey] : undefined;
		PS.teams.loadTeam(team).then(() => {
			this.initializeSelection(team);
			this.forceUpdate();
		});
	};
	changeTeammate = (ev: Event) => {
		this.teammate = (ev.target as HTMLInputElement).value;
		const limit = this.selectionLimit();
		this.selectedPokemon = new globalThis.Set(Array.from(this.selectedPokemon).slice(0, limit));
		this.forceUpdate();
	};
	togglePokemon = (ev: Event) => {
		const input = ev.target as HTMLInputElement;
		const index = Number(input.value);
		if (input.checked) {
			if (this.selectedPokemon.size >= this.selectionLimit()) return;
			this.selectedPokemon.add(index);
		} else {
			this.selectedPokemon.delete(index);
		}
		this.forceUpdate();
	};
	addCustomRules(format: string, rules: string[]) {
		if (!rules.length) return format;
		const hasCustomRules = format.includes('@@@');
		return `${format}${hasCustomRules ? ', ' : '@@@ '}${rules.join(', ')}`;
	}
	submit = (ev: Event, validate?: 'validate') => {
		ev.preventDefault();
		let format = this.format;
		// in tournaments, format is the custom name & teamFormat is the original format.
		const teambuilderFormat = PS.teams.teambuilderFormat(this.props.teamFormat || format);
		const teamElement = this.base!.querySelector<HTMLButtonElement>('button[name=team]');
		const teamKey = teamElement!.value;
		const team = teamKey ? PS.teams.byKey[teamKey] : undefined;
		if (!window.BattleFormats[teambuilderFormat]?.team && !team) {
			PS.alert('You need to go into the Teambuilder and build a team for this format.', {
				parentElem: teamElement!,
			});
			return;
		}
		const customRules: string[] = [];
		if (this.customRules) {
			if (this.bestOf) {
				customRules.push(`Best of = ${this.bestOfValue || '3'}`);
			}
			if (this.teraPreview) customRules.push('Tera Type Preview');
			if (this.megaEvolution) customRules.push('Mega Evolution Rule');
			if (this.zPower) customRules.push('Z-Power Rule');
			if (this.gigantamax) customRules.push('Gigantamax Rule');
			if (this.terastal) customRules.push('Terastal Rule');
			customRules.push(...this.customRuleText.split('\n').map(rule => rule.trim()).filter(Boolean));
		}
		if (this.itemClause) {
			customRules.push('Item Clause = 1');
		}
		if (this.multiplayerDoubles) {
			customRules.push('Multiplayer Doubles');
		}
		if (this.tabletopMatch) customRules.push(`Tabletop Match = ${this.tabletopMatch}`);
		format = this.addCustomRules(format, customRules);
		PS.teams.loadTeam(team).then(() => {
			let battleTeam = team;
			let multiplayerOptions: MultiplayerBattleOptions | undefined;
			if (this.multiplayerDoubles && team) {
				const sets = Teams.unpack(team.packedTeam);
				const selectedSets = Array.from(this.selectedPokemon).sort((a, b) => a - b)
					.map(index => sets[index]).filter(Boolean);
				const hasPartner = this.props.multiplayerRole === 'partner' || !!this.teammate.trim();
				const minimum = hasPartner ? 1 : 2;
				const maximum = hasPartner ? 3 : 6;
				if (selectedSets.length < minimum || selectedSets.length > maximum) {
					PS.alert(`Select ${minimum}-${maximum} Pok\u00e9mon for this battle.`, {
						parentElem: teamElement!,
					});
					return;
				}

				let primarySets = selectedSets;
				let partnerSets: typeof selectedSets = [];
				if (!hasPartner) {
					const split = Math.ceil(selectedSets.length / 2);
					primarySets = selectedSets.slice(0, split);
					partnerSets = selectedSets.slice(split);
				}
				battleTeam = { ...team, packedTeam: Teams.pack(primarySets), iconCache: null };
				multiplayerOptions = {
					multiplayerDoubles: true,
					teammate: this.props.multiplayerRole === 'partner' ? '' : this.teammate.trim(),
					partnerTeam: Teams.pack(partnerSets),
				};
			}
			(validate === 'validate' ? this.props.onValidate : this.props.onSubmit)?.(
				ev, format, battleTeam, multiplayerOptions
			);
		});
	};
	toggleCustomRule = (ev: Event) => {
		const checked = (ev.target as HTMLInputElement)?.checked;
		const rule = (ev.target as HTMLInputElement)?.name;
		if (rule === 'terapreview') this.teraPreview = checked;
		if (rule === 'bestof') this.bestOf = checked;
		if (rule === 'megaevolution') this.megaEvolution = checked;
		if (rule === 'zpower') this.zPower = checked;
		if (rule === 'gigantamax') this.gigantamax = checked;
		if (rule === 'terastal') this.terastal = checked;
		if (rule === 'multiplayerdoubles') {
			this.multiplayerDoubles = checked;
			if (checked) {
				const team = this.getSelectedTeam();
				PS.teams.loadTeam(team).then(() => {
					this.initializeSelection(team);
					this.forceUpdate();
				});
			} else {
				this.teammate = '';
				this.selectedPokemon.clear();
			}
			this.forceUpdate();
		}
		if (rule === 'customrules') {
			this.customRules = checked;
			if (!checked) {
				this.bestOf = false;
				this.teraPreview = false;
			}
			this.forceUpdate();
		}
		if (rule === 'itemclause') this.itemClause = checked;
	};
	/** A gimmick chip in the themed challenge card: toggles and redraws, so the chip shows it. */
	toggleChallengeRule = (ev: Event) => {
		this.toggleCustomRule(ev);
		this.forceUpdate();
	};
	setDoubles = (ev: Event) => {
		ev.preventDefault();
		const doubles = (ev.currentTarget as HTMLButtonElement).value === 'doubles';
		if (doubles === this.multiplayerDoubles) return;
		this.toggleCustomRule({ target: { name: 'multiplayerdoubles', checked: doubles } } as unknown as Event);
	};
	/**
	 * The challenge form in the interface themes (Signal Deck, Galar, Unova):
	 * a team, singles or doubles, the gimmicks as chips, and the rest of the
	 * custom rules tucked away. Classic keeps Showdown's own form below.
	 * A challenge already made (incoming, or waiting) shows its rules as chips.
	 */
	renderChallengeCard() {
		const fixed = this.props.format;
		const rules = (fixed || '').split('@@@')[1]?.split(',').map(r => r.trim()).filter(Boolean) || [];
		const ruleChip = (rule: string) => {
			const known: { [id: string]: [string, string] } = {
				megaevolutionrule: ['gimmick-mega', 'Mega Evolution'],
				zpowerrule: ['gimmick-z', 'Z-Moves'],
				gigantamaxrule: ['gimmick-gmax', 'Gigantamax'],
				terastalrule: ['gimmick-tera', 'Terastallize'],
				multiplayerdoubles: ['', 'Doubles'],
			};
			if (/^tabletop\s*match/i.test(rule)) return <li class="cc-rule cc-rule-match">Tournament match</li>;
			const [cls, label] = known[toID(rule)] || ['', rule];
			return <li class={`cc-rule ${cls}`}>{label}</li>;
		};
		const gimmick = (name: string, cls: string, label: string, on: boolean) => <label
			class={`megaevo ${cls}${on ? ' cur' : ''}`}
		>
			<input type="checkbox" name={name} checked={on} onChange={this.toggleChallengeRule} /> {label}
		</label>;
		return <form class={`${this.props.class || ''} challenge-card`} onSubmit={this.submit} onClick={this.handleClick}>
			{!fixed && this.renderMatchNote()}
			{fixed && <div class="cc-section">
				<span class="cc-label">Rules</span>
				<ul class="cc-rules">{rules.length ? rules.map(ruleChip) : <li class="cc-rule">Standard</li>}</ul>
			</div>}
			<div class="cc-section">
				<span class="cc-label">Your team</span>
				<TeamDropdown format={this.props.teamFormat || this.format} onChange={this.changeTeam} />
			</div>
			{!fixed && <div class="cc-section">
				<span class="cc-label">Battle</span>
				<div class="cc-segment" role="group" aria-label="Singles or doubles">
					<button type="button" value="singles" class={!this.multiplayerDoubles ? 'cur' : ''}
						aria-pressed={!this.multiplayerDoubles ? 'true' : 'false'} onClick={this.setDoubles}>Singles</button>
					<button type="button" value="doubles" class={this.multiplayerDoubles ? 'cur' : ''}
						aria-pressed={this.multiplayerDoubles ? 'true' : 'false'} onClick={this.setDoubles}>Doubles</button>
				</div>
			</div>}
			{this.multiplayerDoubles && <MultiplayerTeamPicker
				team={this.getSelectedTeam()} selected={this.selectedPokemon} limit={this.selectionLimit()}
				role={this.props.multiplayerRole} teammate={this.teammate}
				onChangeTeammate={this.changeTeammate} onTogglePokemon={this.togglePokemon}
			/>}
			{!fixed && <div class="cc-section">
				<span class="cc-label">Gimmicks</span>
				<div class="cc-gimmicks megaevo-box">
					{gimmick('megaevolution', 'gimmick-mega', 'Mega Evolution', this.megaEvolution)}
					{gimmick('zpower', 'gimmick-z', 'Z-Moves', this.zPower)}
					{gimmick('gigantamax', 'gimmick-gmax', 'Gigantamax', this.gigantamax)}
					{gimmick('terastal', 'gimmick-tera', 'Terastallize', this.terastal)}
				</div>
			</div>}
			{!fixed && <details class="cc-more">
				<summary>More rules</summary>
				<textarea
					name="customrules" class="textbox" rows={2} placeholder="Other rules, separated by commas"
					value={this.customRuleText} onInput={this.changeCustomRules}
				/>
			</details>}
			<div class="cc-actions">{this.props.children}</div>
		</form>;
	}
	/** Which tournament match this challenge is, when it is one. */
	renderMatchNote() {
		if (!this.tabletopMatch) return null;
		const match = PSTabletop.myMatches.find(m => `${m.match_id}` === this.tabletopMatch);
		return <p class="cc-match-note">
			<i class="fa fa-trophy" aria-hidden></i> {}
			<strong>{match ? `${match.tournament}: ${match.label}` : 'Tournament match'}</strong>
			<small>The result goes to the bracket on its own.</small>
		</p>;
	}
	handleClick = (ev: Event) => {
		let target = ev.target as HTMLButtonElement | null;
		while (target && target !== this.base) {
			if (target.tagName === 'BUTTON' && target.name === 'validate') {
				this.submit(ev, 'validate');
				return;
			}
			target = target.parentNode as HTMLButtonElement | null;
		}
	};
	render() {
		if (window.BattleFormats) {
			this.format ||= this.props.defaultFormat || '';
			if (!this.format) {
				const defaultId = `gen${Dex.gen}randombattle`;
				// This deployment removed the official format list (see
				// config/formats.ts) in favor of a single custom format, so
				// the usual hardcoded default won't exist.
				this.format = window.BattleFormats[defaultId] ? defaultId : 'gen9campaignprimary';

				const starredPrefs = PS.prefs.starredformats || {};
				// .reverse() because the newest starred format should be the default one
				const starred = Object.keys(starredPrefs).filter(id => starredPrefs[id] === true).reverse();
				for (let id of starred) {
					let format = window.BattleFormats[id];
					if (!format) continue;
					if (this.props.selectType === 'challenge' && format?.challengeShow === false) continue;
					if (this.props.selectType === 'search' && format?.searchShow === false) continue;
					if (this.props.selectType === 'teambuilder' && format?.team) continue;
					this.format = id;
					break;
				}
			}
		}
		if (this.props.defaultFormat?.startsWith('!!')) {
			// The !! means that it overrides any current format, and will only be
			// sent as a prop once
			this.setFormat(this.props.defaultFormat.slice(2));
		}
		if (this.props.format && this.format !== this.props.format) this.setFormat(this.props.format);
		if (!this.props.format && this.format.includes('@@@')) this.setFormat(this.format);
		const formatId = toID(this.format.split('@@@')[0]);
		const format = window.BattleFormats[formatId];
		const showCustomRules = this.props.selectType === 'challenge' && !this.props.format;
		if (this.props.selectType === 'challenge' && interfaceTheme() !== 'classic') return this.renderChallengeCard();
		return <form class={this.props.class} onSubmit={this.submit} onClick={this.handleClick}>
			{!this.props.hideFormat && !(onlyFormat() && this.format.split('@@@')[0] === onlyFormat()) && <p>
				<label class="label">
					Format:<br />
					<FormatDropdown
						selectType={this.props.selectType} format={this.format}
						onChange={this.props.format ? undefined : this.changeFormat}
					/>
				</label>
			</p>}
			{!this.props.format && this.renderMatchNote()}
			<p>
				<label class="label">
					Team:<br />
					<TeamDropdown format={this.props.teamFormat || this.format} onChange={this.changeTeam} />
				</label>
			</p>
			{this.props.selectType === 'challenge' && (!this.props.format || this.multiplayerDoubles) && <>
				{!this.props.format && <p>
					<label class="checkbox"><input
						type="checkbox" name="multiplayerdoubles" checked={this.multiplayerDoubles}
						onChange={this.toggleCustomRule}
					/> Doubles battle</label>
				</p>}
				{this.multiplayerDoubles && <MultiplayerTeamPicker
					team={this.getSelectedTeam()} selected={this.selectedPokemon} limit={this.selectionLimit()}
					role={this.props.multiplayerRole} teammate={this.teammate}
					onChangeTeammate={this.changeTeammate} onTogglePokemon={this.togglePokemon}
				/>}
			</>}
			{showCustomRules && (!this.customRules ? <p>
				<label class="checkbox"><input
					type="checkbox" name="customrules" checked={this.customRules} onChange={this.toggleCustomRule}
				/> Custom rules</label>
			</p> : <fieldset class="fieldset">
				<legend><label class="checkbox"><input
					type="checkbox" name="customrules" checked={this.customRules} onChange={this.toggleCustomRule}
				/> Custom rules</label></legend>
				{(format?.bestOfDefault || this.bestOf) && <p>
					<label class="checkbox">
						<input
							type="checkbox" name="bestof" checked={this.bestOf} onChange={this.toggleCustomRule}
						/>
						<abbr title="Start a team-locked best-of-n series">Best-of-<input
							name="bestofvalue" type="number" min="3" max="9" step="2" value={this.bestOfValue}
							onInput={this.changeBestOfValue}
							style="width: 28px; vertical-align: initial;"
						/></abbr></label>
				</p>}
				{(format?.teraPreviewDefault || this.teraPreview) && <p>
					<label class="checkbox"><input
						type="checkbox" name="terapreview" checked={this.teraPreview} onChange={this.toggleCustomRule}
					/> Tera Type Preview</label>
				</p>}
				{(format?.itemClauseDefault || this.itemClause) && <p>
					<label class="checkbox"><input
						type="checkbox" name="itemclause" checked={this.itemClause} onChange={this.toggleCustomRule}
					/> Item Clause</label>
				</p>}
				<p style="border-top:1px solid rgba(0,0,0,.2);margin-top:4px;padding-top:4px">
					<label class="checkbox"><input
						type="checkbox" name="megaevolution" checked={this.megaEvolution} onChange={this.toggleCustomRule}
					/> Mega Evolution</label>{' '}
					<label class="checkbox"><input
						type="checkbox" name="zpower" checked={this.zPower} onChange={this.toggleCustomRule}
					/> Z-Moves</label>{' '}
					<label class="checkbox"><input
						type="checkbox" name="gigantamax" checked={this.gigantamax} onChange={this.toggleCustomRule}
					/> Gigantamax</label>{' '}
					<label class="checkbox"><input
						type="checkbox" name="terastal" checked={this.terastal} onChange={this.toggleCustomRule}
					/> Terastallize</label>
				</p>
				<textarea
					name="customrules" class="textbox" rows={3} placeholder="Rules separated by commas or lines"
					value={this.customRuleText} onInput={this.changeCustomRules}
					style="min-height:3em"
				/>
				<small><a
					href="https://github.com/smogon/pokemon-showdown/blob/master/config/CUSTOM-RULES.md" target="_blank"
				>Custom rules guide</a></small>
			</fieldset>)}
			<p>{this.props.children}</p>
		</form>;
	}
}

/**
 * Tournaments: the brackets the GM runs from Tabletop's GM Desk, for the
 * player's campaigns. Read-only here; Your match on the home screen (and at
 * the top of this page) is where a player challenges.
 */
class TournamentsPanel extends PSRoomPanel {
	static readonly id = 'tournaments';
	static readonly routes = ['tournaments'];
	static readonly title = 'Tournaments';
	static readonly icon = <i class="fa fa-sitemap" aria-hidden></i>;
	override componentDidMount() {
		super.componentDidMount();
		void PSTabletop.loadTournaments(true);
	}
	challenge = (ev: Event) => {
		PSTabletop.challengeMatch(Number((ev.currentTarget as HTMLButtonElement).value));
	};
	/** Console mode: the tournament whose bracket is open. */
	consoleOpen: number | null = null;
	openConsoleBracket = (ev: Event) => {
		this.consoleOpen = Number((ev.currentTarget as HTMLButtonElement).value);
		this.forceUpdate();
		setTimeout(() => PSConsole.focusIn(this.base?.querySelector<HTMLElement>('.kc-screen')), 40);
	};
	closeConsoleBracket = () => {
		const id = this.consoleOpen;
		this.consoleOpen = null;
		this.forceUpdate();
		setTimeout(() => {
			const card = this.base?.querySelector<HTMLElement>(`.kc-tour[value="${id}"]`);
			if (card) PSConsole.focus(card);
		}, 40);
	};
	renderConsole() {
		const list = PSTabletop.tournaments;
		const mine = PSTabletop.myMatches;
		const open = this.consoleOpen !== null ? list?.find(t => t.id === this.consoleOpen) : null;
		if (open) {
			const champion = open.entries.find(e => e.id === open.champion_id);
			return <PSPanelWrapper room={this.props.room}>
				<div class="kc-screen" data-kc-screen="tournaments" key={`bracket${open.id}`}>
					<ConsoleScreenHead
						icon="sitemap" title={open.name}
						sub={<>{open.campaign} &middot; {open.format === 'double' ? 'Double elimination' : 'Single elimination'} &middot; {}
							{open.entries.length} trainers</>}
					>
						<button class="button kc-back" onClick={this.closeConsoleBracket} data-console-back>
							<i class="fa fa-chevron-left" aria-hidden></i> Back
						</button>
					</ConsoleScreenHead>
					{champion && <p class="kc-champion"><i class="fa fa-trophy" aria-hidden></i> Champion: <strong>{champion.name}</strong></p>}
					{open.note && <p class="kc-note">{open.note}</p>}
					<div class="kc-bracket">{this.renderBracket(open)}</div>
				</div>
			</PSPanelWrapper>;
		}
		const online = new Set((PS.mainmenu.trainersCache || []).map(t => t.userid));
		let i = 0;
		return <PSPanelWrapper room={this.props.room}>
			<div class="kc-screen" data-kc-screen="tournaments" key="list">
				<ConsoleScreenHead
					icon="trophy" title="Tournaments"
					sub={!PS.user.named ? 'Log in to see your campaign\'s tournaments.' :
						mine.length ? (mine.length === 1 ? 'You have a match to play!' : `You have ${mine.length} matches to play!`) :
						list === null ? 'Looking for tournaments...' :
						!list.length ? 'No tournaments right now. Your GM sets them up on Tabletop.' :
						list.length === 1 ? '1 tournament' : `${list.length} tournaments`}
				>
					<ConsoleCloseButton />
				</ConsoleScreenHead>
				{mine.length > 0 && <section class="kc-section is-alert">
					<h3 class="kc-section-title">Your {mine.length > 1 ? 'matches' : 'match'}</h3>
					<div class="kc-cards">{mine.map(m => {
						const opponent = toID(m.opponent.showdown);
						const self = opponent === PS.user.userid;
						const isOnline = online.has(opponent);
						return <div class="kc-card kc-match" style={`--kc-i:${i++}`}>
							<span class="kc-vs" aria-hidden>VS</span>
							<span class="kc-card-text">
								<strong>{m.me.npc ? `${m.me.name} vs. ${m.opponent.name}` : m.opponent.name}</strong>
								<small class="kc-status">{m.tournament} &middot; {m.label}</small>
								<small class={`kc-message${isOnline ? ' is-online' : ''}`}>
									{self ? 'Both sides are yours: set the result on the GM Desk' :
										isOnline ? 'Online now' : `Waiting for ${m.opponent.npc ? m.opponent.showdown : m.opponent.name} to come online`}
								</small>
							</span>
							<span class="kc-card-actions">
								<button
									class="button kc-act is-primary" value={m.match_id} onClick={this.challenge}
									disabled={self || !isOnline} data-console-default={i === 1 || undefined}
								><i class="fa fa-bolt" aria-hidden></i> Challenge</button>
							</span>
						</div>;
					})}</div>
				</section>}
				{list && list.length > 0 && <section class="kc-section">
					<h3 class="kc-section-title">Brackets</h3>
					<div class="kc-cards">{list.map(t => {
						const champion = t.entries.find(e => e.id === t.champion_id);
						return <button class={`kc-card kc-tour is-${t.status}`} value={t.id} onClick={this.openConsoleBracket} style={`--kc-i:${i++}`}>
							<span class="kc-emblem-sm" aria-hidden><i class={`fa fa-${t.status === 'complete' ? 'trophy' : 'sitemap'}`}></i></span>
							<span class="kc-card-text">
								<strong>{t.name}</strong>
								<small class="kc-status">{t.status === 'complete' ? (champion ? `Champion: ${champion.name}` : 'Finished') : 'Under way'}</small>
								<small class="kc-message">
									{t.campaign} &middot; {t.format === 'double' ? 'Double elimination' : 'Single elimination'} &middot; {t.entries.length} trainers
								</small>
							</span>
							<i class="fa fa-chevron-right kc-go" aria-hidden></i>
						</button>;
					})}</div>
				</section>}
			</div>
		</PSPanelWrapper>;
	}
	renderSlot(t: TabletopTournament, m: TabletopBracketMatch, id: number | null) {
		if (id === null) return <div class="tt-slot is-tbd"><span>To be decided</span></div>;
		if (id === 0) return <div class="tt-slot is-bye"><span>Bye</span></div>;
		const entry = t.entries.find(e => e.id === id);
		const won = (m.state === 'done' || m.state === 'bye') && m.winner === id;
		const lost = m.state === 'done' && m.winner !== id;
		return <div class={`tt-slot${won ? ' is-won' : ''}${lost ? ' is-lost' : ''}`}>
			<small>{entry?.seed}</small>
			<span>{entry?.name || '?'}{entry?.npc && <em class="tt-npc">NPC</em>}</span>
			{won && <i class="fa fa-check" aria-label="Winner"></i>}
		</div>;
	}
	renderBracket(t: TabletopTournament) {
		const parts: [string, string][] = t.format === 'double' ?
			[['W', 'Winners bracket'], ['L', 'Losers bracket'], ['F', '']] : [['W', '']];
		return parts.map(([key, title]) => {
			const list = t.matches.filter(m => m.bracket === key && m.state !== 'void' &&
				!(m.code === 'GF2' && m.state === 'waiting'));
			if (!list.length) return null;
			const rounds = Array.from(new globalThis.Set(list.map(m => m.round))).sort((a, b) => a - b);
			return <div class="tt-part">
				{title && <h3>{title}</h3>}
				<div class="tt-rounds">
					{rounds.map(r => {
						const matches = list.filter(m => m.round === r);
						return <div class="tt-round">
							<h4>{matches[0].label}</h4>
							<div class="tt-round-list">
								{matches.map(m => <div class={`tt-match is-${m.state}`}>
									{this.renderSlot(t, m, m.a)}
									{this.renderSlot(t, m, m.b)}
								</div>)}
							</div>
						</div>;
					})}
				</div>
			</div>;
		});
	}
	override render() {
		if (menuScreensOn()) return this.renderConsole();
		const list = PSTabletop.tournaments;
		const mine = PSTabletop.myMatches;
		return <PSPanelWrapper room={this.props.room}>
			<div class="pad tournaments-page">
				<h2>Tournaments</h2>
				{mine.length > 0 && <div class="tt-mine">
					{mine.map(m => <div class="your-match">
						<span class="your-match-info">
							<small>{m.tournament} &middot; {m.label}</small>
							<strong>{m.me.npc ? `${m.me.name} vs. ${m.opponent.name}` : `vs. ${m.opponent.name}`}</strong>
						</span>
						<button
							class="button your-match-go" value={m.match_id} onClick={this.challenge}
							disabled={toID(m.opponent.showdown) === PS.user.userid}
						><strong>Challenge</strong></button>
					</div>)}
				</div>}
				{!PS.user.named ? <p><em>Log in to see your campaign's tournaments.</em></p> :
					list === null ? <p><em>Loading...</em></p> :
					!list.length ? <p class="tt-empty"><em>No tournaments right now. Your GM sets them up on Tabletop.</em></p> :
					list.map(t => {
						const champion = t.entries.find(e => e.id === t.champion_id);
						return <section class={`tt-tournament is-${t.status}`}>
							<header>
								<h3>{t.name}</h3>
								<small>
									{t.campaign} &middot; {t.format === 'double' ? 'Double elimination' : 'Single elimination'} &middot; {}
									{t.entries.length} trainers
								</small>
								{champion && <p class="tt-champion"><i class="fa fa-trophy" aria-hidden></i> Champion: <strong>{champion.name}</strong></p>}
								{t.note && <p class="tt-note">{t.note}</p>}
							</header>
							{this.renderBracket(t)}
						</section>;
					})}
			</div>
		</PSPanelWrapper>;
	}
}

PS.addRoomType(NewsPanel, MainMenuPanel, TournamentsPanel);
